import React from 'react';

import { VISIBLE_USERS_COLS_INITIAL_STATE } from '../constants/users';

interface IProps {
  initialState: Record<string, boolean>;
  requiredColumnIds?: string[];
  onChange: (newState: Record<string, boolean>) => void;
}

const useColumnsVisibility = ({ initialState, requiredColumnIds = [], onChange }: IProps) => {
  const [visibleColumns, setVisibleColumns] = React.useState<Record<string, boolean>>(initialState);
  const [columnSearchQuery, setColumnSearchQuery] = React.useState('');

  const handleColumnVisibilityChange = React.useCallback(
    (columnId: string) => {
      const newVal = {
        ...visibleColumns,
        [columnId]: !visibleColumns[columnId],
      };

      setVisibleColumns((prev) => ({
        ...prev,
        [columnId]: !prev[columnId],
      }));
      onChange?.(newVal);
    },
    [onChange, visibleColumns],
  );

  const handleToggleAllColumns = React.useCallback(
    (showAll: boolean) => {
      const newState = { ...visibleColumns };

      Object.keys(newState).forEach((key) => {
        if (!requiredColumnIds.includes(key)) {
          newState[key] = showAll;
        }
      });

      setVisibleColumns(newState);
      onChange?.(newState);
    },
    [visibleColumns, onChange, requiredColumnIds],
  );

  const handleResetColumns = React.useCallback(() => {
    setVisibleColumns(VISIBLE_USERS_COLS_INITIAL_STATE);
    onChange?.(VISIBLE_USERS_COLS_INITIAL_STATE);
  }, [onChange]);

  const areAllColumnsVisible = React.useMemo(() => {
    const optionalColumnIds = Object.keys(visibleColumns).filter((id) => !requiredColumnIds.includes(id));

    return optionalColumnIds.every((id) => visibleColumns[id]);
  }, [visibleColumns, requiredColumnIds]);

  return {
    visibleColumns,
    columnSearchQuery,
    setColumnSearchQuery,
    handleColumnVisibilityChange,
    handleToggleAllColumns,
    handleResetColumns,
    areAllColumnsVisible,
  };
};

export default useColumnsVisibility;
